import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  GARMENT_CATEGORIES,
  OCCASIONS,
  SEASONS,
  type GarmentCategory,
  type Occasion,
  type Season,
  type WardrobeItem,
} from '../shared/wardrobe';
import { Icon, type IconName } from './components/Icon';
import { AppStatus } from './components/AppStatus';
import { BrandsPage } from './components/BrandsPage';
import { InsightsPage } from './components/InsightsPage';
import { OutfitDetailPage, OutfitsPage, OutfitStudioPage } from './components/OutfitStudioPage';
import { PlannerPage } from './components/PlannerPage';
import { SalesPage } from './components/SalesPage';
import { SmartDetectorPage } from './components/SmartDetectorPage';
import { ShoppingLinkImporter } from './components/ShoppingLinkImporter';
import { StylistPage } from './components/StylistPage';
import { TodayRecommendationsPage } from './components/TodayRecommendationsPage';
import { TryOnPage } from './components/TryOnPage';
import { getSession, login, logout, type SessionUser } from './lib/api';
import { startGarmentImageJob } from './lib/imageProcessing';
import {
  fetchStudioPreviewImage,
  startStudioPreview,
  waitForStudioPreview,
} from './lib/studioPreview';
import {
  createWardrobeItem,
  deleteWardrobeItem,
  getWardrobeItem,
  listWardrobeItems,
  updateWardrobeItem,
} from './lib/items';
import {
  clearPrivateDeviceData,
  clearUploadDraft,
  readUploadDraft,
  saveUploadDraft,
} from './lib/offlineCloset';
import { deletePrivateClosetData, downloadPrivateExport } from './lib/privacy';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'anonymous' }
  | { kind: 'authenticated'; user: SessionUser }
  | { kind: 'unavailable'; message: string };

interface NavigationItem {
  label: string;
  path: string;
  icon: IconName;
}

const mainNavigation: NavigationItem[] = [
  { label: 'Today', path: '/today', icon: 'home' },
  { label: 'Stylist', path: '/stylist', icon: 'sparkles' },
  { label: 'Try on', path: '/try-on', icon: 'outfit' },
  { label: 'Closet', path: '/closet', icon: 'closet' },
  { label: 'Outfits', path: '/outfits', icon: 'outfit' },
  { label: 'Planner', path: '/planner', icon: 'calendar' },
  { label: 'Insights', path: '/insights', icon: 'chart' },
  { label: 'Sales', path: '/sales', icon: 'search' },
];

const mobileNavigation: NavigationItem[] = [
  mainNavigation.find((item) => item.label === 'Today') as NavigationItem,
  mainNavigation.find((item) => item.label === 'Closet') as NavigationItem,
  mainNavigation.find((item) => item.label === 'Outfits') as NavigationItem,
  { label: 'Add', path: '/add', icon: 'add' },
  mainNavigation.find((item) => item.label === 'Planner') as NavigationItem,
  mainNavigation.find((item) => item.label === 'Try on') as NavigationItem,
  mainNavigation.find((item) => item.label === 'Sales') as NavigationItem,
];

export function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: 'loading' });

  const refreshSession = useCallback(async () => {
    setAuth({ kind: 'loading' });
    const result = await getSession();

    if (result.status === 'authenticated') {
      setAuth({ kind: 'authenticated', user: result.user });
    } else if (result.status === 'anonymous') {
      setAuth({ kind: 'anonymous' });
    } else {
      setAuth({ kind: 'unavailable', message: result.message });
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  if (auth.kind === 'loading') {
    return <LoadingScreen />;
  }

  if (auth.kind === 'unavailable') {
    return <ConnectionScreen message={auth.message} onRetry={() => void refreshSession()} />;
  }

  if (auth.kind === 'anonymous') {
    return <LoginScreen onAuthenticated={(user) => setAuth({ kind: 'authenticated', user })} />;
  }

  return (
    <PrivateApp
      user={auth.user}
      onLoggedOut={() => {
        setAuth({ kind: 'anonymous' });
      }}
    />
  );
}

function LoadingScreen() {
  return (
    <main className="center-screen" aria-live="polite">
      <div className="brand-mark brand-mark--loading">
        <Icon name="hanger" size={29} />
      </div>
      <p className="eyebrow">Her Closet</p>
      <p className="muted-copy">Opening your wardrobe…</p>
    </main>
  );
}

function ConnectionScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="center-screen">
      <div className="brand-mark">
        <Icon name="hanger" size={29} />
      </div>
      <p className="eyebrow">Her Closet</p>
      <h1 className="connection-title">Your closet is taking a moment.</h1>
      <p className="muted-copy connection-copy">{message}</p>
      <button className="button button--primary" type="button" onClick={onRetry}>
        Try again
      </button>
    </main>
  );
}

function LoginScreen({ onAuthenticated }: { onAuthenticated: (user: SessionUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      onAuthenticated(await login(username.trim(), password));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story" aria-label="Welcome">
        <div className="login-brand">
          <span className="brand-mark brand-mark--light">
            <Icon name="hanger" size={27} />
          </span>
          <span>Her Closet</span>
        </div>

        <div className="login-story__content">
          <p className="eyebrow eyebrow--light">A wardrobe made just for you</p>
          <h1>Everything you love to wear, beautifully remembered.</h1>
          <p>Plan a look, rediscover an old favourite, and make getting dressed feel effortless.</p>
        </div>

        <div className="login-collage" aria-hidden="true">
          <div className="fabric-card fabric-card--rose">
            <span>date night</span>
          </div>
          <div className="fabric-card fabric-card--sage">
            <Icon name="sparkles" size={30} />
            <span>your style</span>
          </div>
          <div className="fabric-card fabric-card--plum">
            <span>favourites</span>
          </div>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="mobile-login-brand">
            <span className="brand-mark">
              <Icon name="hanger" size={27} />
            </span>
            <span>Her Closet</span>
          </div>

          <p className="eyebrow">Private wardrobe</p>
          <h2>Welcome back</h2>
          <p className="login-intro">Sign in once and your closet stays ready on this device.</p>

          <form className="login-form" onSubmit={(event) => void handleSubmit(event)}>
            <label>
              <span>Username</span>
              <input
                autoCapitalize="none"
                autoComplete="username"
                name="username"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>

            <label>
              <span>Password</span>
              <input
                autoComplete="current-password"
                name="password"
                required
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}

            <button className="button button--primary button--wide" disabled={submitting}>
              {submitting ? 'Opening your closet…' : 'Open my closet'}
              {!submitting ? <Icon name="arrow" size={18} /> : null}
            </button>
          </form>

          <p className="login-privacy">
            <Icon name="heart" size={15} />
            Private by design. Only you can see what is inside.
          </p>
        </div>
      </section>
    </main>
  );
}

function PrivateApp({ user, onLoggedOut }: { user: SessionUser; onLoggedOut: () => void }) {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      await clearPrivateDeviceData();
      onLoggedOut();
      await navigate('/today');
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <AppStatus />
      <aside className="sidebar">
        <NavLink className="sidebar-brand" to="/today">
          <span className="brand-mark">
            <Icon name="hanger" size={25} />
          </span>
          <span>
            <strong>Her Closet</strong>
            <small>private wardrobe</small>
          </span>
        </NavLink>

        <nav className="desktop-nav" aria-label="Main navigation">
          {mainNavigation.map((item) => (
            <NavLink
              className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
              key={item.path}
              to={item.path}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <NavLink className="add-item-button" to="/add">
          <Icon name="add" size={19} />
          Add an item
        </NavLink>

        <NavLink className="profile-card" to="/settings">
          <Avatar name={user.displayName} />
          <span>
            <strong>{user.displayName}</strong>
            <small>@{user.username}</small>
          </span>
          <Icon name="settings" size={18} />
        </NavLink>
      </aside>

      <div className="main-column">
        <header className="mobile-header">
          <NavLink className="mobile-brand" to="/today">
            <span className="brand-mark brand-mark--small">
              <Icon name="hanger" size={22} />
            </span>
            <strong>Her Closet</strong>
          </NavLink>
          <NavLink aria-label="Settings" to="/settings">
            <Avatar name={user.displayName} small />
          </NavLink>
        </header>

        <main className="page-content" id="main-content">
          <Routes>
            <Route element={<Navigate replace to="/today" />} path="/" />
            <Route element={<TodayRecommendationsPage user={user} />} path="/today" />
            <Route element={<TodayRecommendationsPage styleMode user={user} />} path="/style" />
            <Route element={<StylistPage />} path="/stylist" />
            <Route element={<TryOnPage />} path="/try-on" />
            <Route element={<ClosetPage />} path="/closet" />
            <Route element={<GarmentDetailPage />} path="/closet/:itemId" />
            <Route element={<AddItemPage />} path="/add" />
            <Route element={<SmartDetectorPage />} path="/add/smart" />
            <Route element={<OutfitsPage />} path="/outfits" />
            <Route element={<OutfitStudioPage />} path="/outfits/new" />
            <Route element={<OutfitDetailPage />} path="/outfits/:outfitId" />
            <Route element={<OutfitStudioPage />} path="/outfits/:outfitId/edit" />
            <Route element={<PlannerPage />} path="/planner" />
            <Route element={<InsightsPage />} path="/insights" />
            <Route element={<SalesPage />} path="/sales" />
            <Route element={<BrandsPage />} path="/sales/brands" />
            <Route
              element={<SettingsPage user={user} loggingOut={loggingOut} onLogout={handleLogout} />}
              path="/settings"
            />
            <Route element={<Navigate replace to="/today" />} path="*" />
          </Routes>
        </main>
      </div>

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {mobileNavigation.map((item) => (
          <div className="mobile-nav-slot" key={item.path}>
            {item.label === 'Add' ? (
              <NavLink aria-label="Add an item" className="mobile-add" to="/add">
                <Icon name="add" size={25} />
              </NavLink>
            ) : (
              <NavLink
                className={({ isActive }) => `mobile-nav-link${isActive ? ' is-active' : ''}`}
                to={item.path}
              >
                <Icon name={item.icon} size={21} />
                <span>{item.label}</span>
              </NavLink>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

function Avatar({ name, small = false }: { name: string; small?: boolean }) {
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return <span className={`avatar${small ? ' avatar--small' : ''}`}>{initials || 'HC'}</span>;
}

function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

function ClosetPage() {
  const [items, setItems] = useState<WardrobeItem[]>([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<GarmentCategory | ''>('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      setItems(
        await listWardrobeItems({
          search,
          category,
          favorite: favoritesOnly,
          archived: archivedOnly,
        }),
      );
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'Your wardrobe could not load.');
    } finally {
      setLoading(false);
    }
  }, [archivedOnly, category, favoritesOnly, search]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadItems(), 220);
    return () => window.clearTimeout(timeout);
  }, [loadItems]);

  async function toggleFavorite(item: WardrobeItem) {
    const optimistic = { ...item, favorite: !item.favorite };
    setItems((current) => current.map((entry) => (entry.id === item.id ? optimistic : entry)));

    try {
      const updated = await updateWardrobeItem(item.id, { favorite: optimistic.favorite });
      setItems((current) => current.map((entry) => (entry.id === item.id ? updated : entry)));
    } catch (caught) {
      setItems((current) => current.map((entry) => (entry.id === item.id ? item : entry)));
      setErrorMessage(caught instanceof Error ? caught.message : 'The favourite could not update.');
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Your wardrobe"
        title="Closet"
        description="Every piece you love, easy to find."
        action={
          <NavLink className="button button--primary desktop-only-button" to="/add">
            <Icon name="add" size={18} />
            Add item
          </NavLink>
        }
      />
      <div className="toolbar">
        <div className="search-field">
          <Icon name="search" size={19} />
          <input
            aria-label="Search closet"
            placeholder="Search name, colour, or brand"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <label className="select-filter">
          <span className="sr-only">Category</span>
          <select
            aria-label="Filter by category"
            value={category}
            onChange={(event) => setCategory(event.target.value as GarmentCategory | '')}
          >
            <option value="">All pieces</option>
            {GARMENT_CATEGORIES.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </label>
        <button
          aria-pressed={favoritesOnly}
          className={`filter-button${favoritesOnly ? ' filter-button--active' : ''}`}
          onClick={() => setFavoritesOnly((current) => !current)}
          type="button"
        >
          Favourites
        </button>
        <button
          aria-pressed={archivedOnly}
          className={`filter-button${archivedOnly ? ' filter-button--active' : ''}`}
          onClick={() => setArchivedOnly((current) => !current)}
          type="button"
        >
          Archived
        </button>
      </div>

      {errorMessage ? (
        <div className="inline-error" role="alert">
          <span>{errorMessage}</span>
          <button type="button" onClick={() => void loadItems()}>
            Try again
          </button>
        </div>
      ) : null}

      {loading ? (
        <WardrobeLoading />
      ) : items.length > 0 ? (
        <>
          <div className="closet-results">
            <span>
              {items.length} {items.length === 1 ? 'piece' : 'pieces'}
            </span>
          </div>
          <div className="garment-grid">
            {items.map((item) => (
              <article className="garment-card" key={item.id}>
                <div className="garment-photo">
                  <NavLink aria-label={`Open ${item.name}`} to={`/closet/${item.id}`}>
                    <img alt={item.name} loading="lazy" src={item.photoUrl} />
                  </NavLink>
                  <button
                    aria-label={
                      item.favorite
                        ? `Remove ${item.name} from favourites`
                        : `Add ${item.name} to favourites`
                    }
                    aria-pressed={item.favorite}
                    className={`favorite-button${item.favorite ? ' is-favorite' : ''}`}
                    type="button"
                    onClick={() => void toggleFavorite(item)}
                  >
                    <Icon name="heart" size={19} />
                  </button>
                </div>
                <div className="garment-card__copy">
                  <div>
                    <h2>
                      <NavLink to={`/closet/${item.id}`}>{item.name}</NavLink>
                    </h2>
                    <p>
                      {item.category} · {item.primaryColor}
                    </p>
                  </div>
                  {item.brand ? <span>{item.brand}</span> : null}
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <EmptyState
          description={
            search || category || favoritesOnly || archivedOnly
              ? 'Try a different search or clear one of the filters.'
              : 'Photograph a favourite garment and it will appear here as a clean, private wardrobe card.'
          }
          icon="closet"
          title={
            search || category || favoritesOnly || archivedOnly
              ? 'No matching pieces'
              : 'Your closet is waiting'
          }
          {...(search || category || favoritesOnly || archivedOnly
            ? {}
            : { action: 'Add your first piece', to: '/add' })}
        />
      )}
    </div>
  );
}

function WardrobeLoading() {
  return (
    <div aria-label="Loading wardrobe" className="garment-grid" role="status">
      {Array.from({ length: 6 }, (_, index) => (
        <div className="garment-card garment-card--loading" key={index}>
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function AddItemPage() {
  const navigate = useNavigate();
  const [sourceMode, setSourceMode] = useState<'photo' | 'link'>('photo');
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadRequestKey = useRef<string>(crypto.randomUUID());
  const [photo, setPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<GarmentCategory>('Tops');
  const [primaryColor, setPrimaryColor] = useState('');
  const [brand, setBrand] = useState('');
  const [material, setMaterial] = useState('');
  const [notes, setNotes] = useState('');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [favorite, setFavorite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadAttempted, setUploadAttempted] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const cleanPreviewSource = useRef<File | null>(null);
  const [cleanPreview, setCleanPreview] = useState<
    { status: 'processing' } | { status: 'completed'; url: string } | { status: 'failed' } | null
  >(null);

  useEffect(() => {
    if (!photo) {
      cleanPreviewSource.current = null;
      setCleanPreview(null);
      return;
    }
    if (cleanPreviewSource.current === photo) return;
    cleanPreviewSource.current = photo;
    setCleanPreview({ status: 'processing' });
    void (async () => {
      try {
        const started = await startStudioPreview(photo, crypto.randomUUID());
        const finished =
          started.status === 'processing' ? await waitForStudioPreview(started.id) : started;
        if (cleanPreviewSource.current !== photo) return;
        setCleanPreview(
          finished.status === 'completed' && finished.previewUrl
            ? { status: 'completed', url: finished.previewUrl }
            : { status: 'failed' },
        );
      } catch {
        if (cleanPreviewSource.current === photo) setCleanPreview({ status: 'failed' });
      }
    })();
  }, [photo]);

  useEffect(() => {
    void readUploadDraft().then((draft) => {
      if (!draft) return;
      uploadRequestKey.current = draft.requestKey;
      setPhoto(
        new File([draft.photo], draft.photoName, {
          type: draft.photoType || draft.photo.type,
        }),
      );
      setName(draft.input.name);
      setCategory(draft.input.category);
      setPrimaryColor(draft.input.primaryColor);
      setBrand(draft.input.brand ?? '');
      setMaterial(draft.input.material ?? '');
      setNotes(draft.input.notes ?? '');
      setSeasons(draft.input.seasons);
      setOccasions(draft.input.occasions);
      setFavorite(Boolean(draft.input.favorite));
      setUploadAttempted(true);
      setRecoveredDraft(true);
    });
  }, []);

  useEffect(() => {
    if (!photo) {
      setPreviewUrl('');
      return;
    }

    const url = URL.createObjectURL(photo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  function toggleValue<T extends string>(value: T, selected: T[], update: (next: T[]) => void) {
    update(
      selected.includes(value) ? selected.filter((entry) => entry !== value) : [...selected, value],
    );
  }

  async function handleAddItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage('');

    if (!photo) {
      setErrorMessage('Choose a garment photo first.');
      fileInput.current?.focus();
      return;
    }

    setSubmitting(true);
    setUploadAttempted(true);
    try {
      const input = {
        name,
        category,
        primaryColor,
        brand,
        material,
        notes,
        seasons,
        occasions,
        favorite,
      };
      await saveUploadDraft({
        input,
        photo,
        photoName: photo.name,
        photoType: photo.type,
        requestKey: uploadRequestKey.current,
      });
      const cleanFile =
        cleanPreview?.status === 'completed'
          ? await fetchStudioPreviewImage(cleanPreview.url).catch(() => null)
          : null;
      const item = await createWardrobeItem(input, cleanFile ?? photo, uploadRequestKey.current);
      if (!cleanFile) {
        // The reviewed clean image was unavailable, so this piece saved with
        // the original photo; the after-save cleanup remains the fallback.
        await startGarmentImageJob(item.id, `${uploadRequestKey.current}-studio`, 'studio-photo', {
          autoApply: true,
        }).catch(() => undefined);
      }
      await clearUploadDraft();
      await navigate('/closet');
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The garment could not be saved.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="New wardrobe piece"
        title="Add an item"
        description={
          sourceMode === 'link'
            ? 'Paste links from different stores. AI prepares each item for your review, then creates a clean garment-only closet image (about $0.04 per piece).'
            : 'Start with one clear photo. A clean garment-only closet image is created automatically (about $0.04), and your original stays private.'
        }
        action={
          sourceMode === 'photo' ? (
            <NavLink className="button button--secondary" to="/add/smart">
              <Icon name="sparkles" size={18} />
              Smart Detector
            </NavLink>
          ) : null
        }
      />
      <section aria-label="Choose how to add an item" className="add-source-switch">
        <button
          aria-pressed={sourceMode === 'photo'}
          className={sourceMode === 'photo' ? 'is-selected' : ''}
          type="button"
          onClick={() => setSourceMode('photo')}
        >
          <Icon name="hanger" size={19} />
          Upload photo
        </button>
        <button
          aria-pressed={sourceMode === 'link'}
          className={sourceMode === 'link' ? 'is-selected' : ''}
          type="button"
          onClick={() => setSourceMode('link')}
        >
          <Icon name="sparkles" size={19} />
          Paste shopping link
        </button>
      </section>

      {sourceMode === 'link' ? (
        <ShoppingLinkImporter onUsePhoto={() => setSourceMode('photo')} />
      ) : (
        <>
          <section className="add-mode-note">
            <span className="card-icon card-icon--sage">
              <Icon name="sparkles" size={20} />
            </span>
            <span>
              <strong>More than one piece in the photo?</strong>
              <small>Smart Detector finds separate garments and asks you to review each one.</small>
            </span>
            <NavLink to="/add/smart">Try Smart Detector</NavLink>
          </section>
          <form className="add-item-layout" onSubmit={(event) => void handleAddItem(event)}>
        <section className="photo-picker-card">
          <input
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            aria-label="Garment photo"
            className="sr-only"
            ref={fileInput}
            type="file"
            onChange={(event) => {
              setPhoto(event.target.files?.[0] ?? null);
              setRecoveredDraft(false);
              void clearUploadDraft();
            }}
          />
          <button
            className={`photo-picker${previewUrl ? ' has-photo' : ''}`}
            type="button"
            onClick={() => fileInput.current?.click()}
          >
            {cleanPreview?.status === 'completed' ? (
              <img alt="Clean garment preview" src={cleanPreview.url} />
            ) : previewUrl ? (
              <img alt="Selected garment preview" src={previewUrl} />
            ) : (
              <>
                <span className="upload-illustration">
                  <Icon name="hanger" size={48} />
                  <span className="upload-sparkle">
                    <Icon name="sparkles" size={22} />
                  </span>
                </span>
                <strong>Choose a garment photo</strong>
                <small>JPEG, PNG, WebP, HEIC or HEIF · up to 12 MB</small>
              </>
            )}
          </button>
          {previewUrl ? (
            <button
              className="replace-photo"
              type="button"
              onClick={() => fileInput.current?.click()}
            >
              Replace photo
            </button>
          ) : null}
          {photo && cleanPreview?.status !== 'completed' ? (
            <p aria-live="polite" className="clean-preview-status" role="status">
              {cleanPreview?.status === 'failed'
                ? 'The clean image could not be prepared; it will be cleaned after saving.'
                : 'Creating the clean garment-only image…'}
            </p>
          ) : null}
          <div className="upload-steps">
            <span>
              <Icon name="check" size={16} /> Private
            </span>
            <span>
              <Icon name="check" size={16} /> Original preserved
            </span>
            <span>
              <Icon name="check" size={16} /> You control details
            </span>
          </div>
        </section>

        <section className="item-form-card">
          {recoveredDraft ? (
            <p className="recovery-note" role="status">
              <Icon name="check" size={17} />
              Your unsent upload was restored on this device.
            </p>
          ) : null}
          <div className="form-section-heading">
            <p className="eyebrow">The essentials</p>
            <h2>Tell your closet what it is</h2>
          </div>

          <div className="field-grid">
            <label className="field field--wide">
              <span>Name</span>
              <input
                maxLength={100}
                placeholder="e.g. Blue linen dress"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Category</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value as GarmentCategory)}
              >
                {GARMENT_CATEGORIES.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Main colour</span>
              <input
                maxLength={40}
                placeholder="e.g. Sky blue"
                required
                value={primaryColor}
                onChange={(event) => setPrimaryColor(event.target.value)}
              />
            </label>
            <label className="field">
              <span>
                Brand <small>optional</small>
              </span>
              <input
                maxLength={80}
                value={brand}
                onChange={(event) => setBrand(event.target.value)}
              />
            </label>
            <label className="field">
              <span>
                Material <small>optional</small>
              </span>
              <input
                maxLength={80}
                value={material}
                onChange={(event) => setMaterial(event.target.value)}
              />
            </label>
          </div>

          <fieldset className="choice-fieldset">
            <legend>Season</legend>
            <div className="choice-chips">
              {SEASONS.map((option) => (
                <button
                  aria-pressed={seasons.includes(option)}
                  className={seasons.includes(option) ? 'is-selected' : ''}
                  key={option}
                  type="button"
                  onClick={() => toggleValue(option, seasons, setSeasons)}
                >
                  {option}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="choice-fieldset">
            <legend>Occasion</legend>
            <div className="choice-chips">
              {OCCASIONS.map((option) => (
                <button
                  aria-pressed={occasions.includes(option)}
                  className={occasions.includes(option) ? 'is-selected' : ''}
                  key={option}
                  type="button"
                  onClick={() => toggleValue(option, occasions, setOccasions)}
                >
                  {option}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="field field--wide">
            <span>
              Notes <small>optional</small>
            </span>
            <textarea
              maxLength={1000}
              placeholder="Fit, styling ideas, or a memory attached to this piece…"
              rows={4}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          <label className="favorite-check">
            <input
              checked={favorite}
              type="checkbox"
              onChange={(event) => setFavorite(event.target.checked)}
            />
            <span className="card-icon card-icon--rose">
              <Icon name="heart" size={20} />
            </span>
            <span>
              <strong>Mark as a favourite</strong>
              <small>Keep it close when building outfits.</small>
            </span>
          </label>

          {errorMessage ? (
            <p className="form-error" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <div className="form-actions">
            <NavLink className="button button--secondary" to="/closet">
              Cancel
            </NavLink>
            <button className="button button--primary" disabled={submitting} type="submit">
              {submitting
                ? 'Saving privately…'
                : uploadAttempted
                  ? 'Retry upload'
                  : 'Add to my closet'}
              {!submitting ? <Icon name="arrow" size={18} /> : null}
            </button>
          </div>
        </section>
          </form>
        </>
      )}
    </div>
  );
}

function importedCurrencyDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

function importedPriceFromMinor(value: number | null, currency: string | null): string {
  if (value === null) return '';
  const digits = importedCurrencyDigits(currency ?? 'INR');
  return (value / 10 ** digits).toFixed(digits);
}

function importedPriceToMinor(value: string, currency: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 10 ** importedCurrencyDigits(currency));
}

function GarmentDetailPage() {
  const { itemId = '' } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<WardrobeItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<GarmentCategory>('Other');
  const [primaryColor, setPrimaryColor] = useState('');
  const [brand, setBrand] = useState('');
  const [material, setMaterial] = useState('');
  const [notes, setNotes] = useState('');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [favorite, setFavorite] = useState(false);
  const [purchasedSize, setPurchasedSize] = useState('');
  const [purchasedColor, setPurchasedColor] = useState('');
  const [listedPrice, setListedPrice] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const loadItem = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const loaded = await getWardrobeItem(itemId);
      setItem(loaded);
      setName(loaded.name);
      setCategory(loaded.category);
      setPrimaryColor(loaded.primaryColor);
      setBrand(loaded.brand ?? '');
      setMaterial(loaded.material ?? '');
      setNotes(loaded.notes ?? '');
      setSeasons(loaded.seasons);
      setOccasions(loaded.occasions);
      setFavorite(loaded.favorite);
      setPurchasedSize(loaded.source?.size ?? '');
      setPurchasedColor(loaded.source?.color ?? '');
      setListedPrice(
        importedPriceFromMinor(
          loaded.source?.listedPriceMinor ?? null,
          loaded.source?.currency ?? null,
        ),
      );
      setCurrency(loaded.source?.currency ?? 'INR');
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The garment could not load.');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    void loadItem();
  }, [loadItem]);

  async function saveDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!item) return;

    setSaving(true);
    setErrorMessage('');
    try {
      const listedPriceMinor = importedPriceToMinor(listedPrice, currency);
      const updated = await updateWardrobeItem(item.id, {
        name,
        category,
        primaryColor,
        brand,
        material,
        notes,
        seasons,
        occasions,
        favorite,
        ...(item.source?.kind === 'shopping-link'
          ? {
              purchasedSize: purchasedSize.trim() || null,
              purchasedColor: purchasedColor.trim() || null,
              listedPriceMinor,
              currency: listedPriceMinor === null ? null : currency.trim().toUpperCase() || 'INR',
            }
          : {}),
      });
      setItem(updated);
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The garment could not update.');
    } finally {
      setSaving(false);
    }
  }

  function toggleDetailValue<T extends string>(
    value: T,
    selected: T[],
    update: (next: T[]) => void,
  ) {
    update(
      selected.includes(value) ? selected.filter((entry) => entry !== value) : [...selected, value],
    );
  }

  async function toggleArchive() {
    if (!item) return;
    setSaving(true);
    setErrorMessage('');
    try {
      const updated = await updateWardrobeItem(item.id, { archived: !item.archived });
      setItem(updated);
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The garment could not update.');
    } finally {
      setSaving(false);
    }
  }

  async function removeItem() {
    if (!item) return;
    // Browser dialogs are blocked in installed/embedded app contexts, so
    // destructive actions confirm with an explicit second tap instead.
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      setErrorMessage(
        `Deleting “${item.name}” also removes its private photo. Tap Delete again to confirm.`,
      );
      return;
    }
    setConfirmingDelete(false);
    setErrorMessage('');
    setSaving(true);
    setErrorMessage('');
    try {
      await deleteWardrobeItem(item.id);
      await navigate('/closet');
    } catch (caught) {
      setErrorMessage(
        caught instanceof Error ? caught.message : 'The garment could not be deleted.',
      );
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page page--narrow">
        <WardrobeLoading />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="page page--narrow">
        <EmptyState
          action="Back to closet"
          description={errorMessage || 'This item may have been removed.'}
          icon="closet"
          title="Garment not found"
          to="/closet"
        />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={item.archived ? 'Archived piece' : item.category}
        title={item.name}
        description="Keep the details useful to you—there are no required fashion rules."
        action={
          <div className="page-header-actions">
            {!item.archived ? (
              <NavLink className="button button--primary" to={`/style?item=${item.id}`}>
                <Icon name="sparkles" size={18} />
                Style this item
              </NavLink>
            ) : null}
            <NavLink className="button button--secondary" to="/closet">
              Back to closet
            </NavLink>
          </div>
        }
      />

      <div className="garment-detail-layout">
        <section className="garment-detail-photo">
          <img alt={item.name} src={`${item.photoUrl}?v=${Math.max(item.updatedAt, 0)}`} />
          <div className="garment-detail-tags">
            <span>{item.primaryColor}</span>
            {item.seasons.map((season) => (
              <span key={season}>{season}</span>
            ))}
          </div>
          {item.source?.kind === 'shopping-link' ? (
            <aside className="shopping-source-card">
              <div>
                <p className="eyebrow">Purchased online</p>
                <strong>{item.source.store}</strong>
              </div>
              <a href={item.source.url} rel="noreferrer" target="_blank">
                View original product
              </a>
            </aside>
          ) : null}
        </section>

        <form className="item-form-card" onSubmit={(event) => void saveDetails(event)}>
          <div className="form-section-heading">
            <p className="eyebrow">Garment details</p>
            <h2>Edit this piece</h2>
          </div>
          <div className="field-grid">
            <label className="field field--wide">
              <span>Name</span>
              <input required value={name} onChange={(event) => setName(event.target.value)} />
            </label>
            <label className="field">
              <span>Category</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value as GarmentCategory)}
              >
                {GARMENT_CATEGORIES.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Main colour</span>
              <input
                required
                value={primaryColor}
                onChange={(event) => setPrimaryColor(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Brand</span>
              <input value={brand} onChange={(event) => setBrand(event.target.value)} />
            </label>
            <label className="field">
              <span>Material</span>
              <input value={material} onChange={(event) => setMaterial(event.target.value)} />
            </label>
            <label className="field field--wide">
              <span>Notes</span>
              <textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>
            {item.source?.kind === 'shopping-link' ? (
              <>
                <label className="field">
                  <span>Purchased size</span>
                  <input
                    maxLength={80}
                    value={purchasedSize}
                    onChange={(event) => setPurchasedSize(event.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Selected colour</span>
                  <input
                    maxLength={80}
                    value={purchasedColor}
                    onChange={(event) => setPurchasedColor(event.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Listed price</span>
                  <input
                    inputMode="decimal"
                    value={listedPrice}
                    onChange={(event) => setListedPrice(event.target.value)}
                  />
                </label>
                <label className="field">
                  <span>Currency</span>
                  <input
                    maxLength={3}
                    value={currency}
                    onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                  />
                </label>
              </>
            ) : null}
          </div>

          <fieldset className="choice-fieldset">
            <legend>Season</legend>
            <div className="choice-chips">
              {SEASONS.map((option) => (
                <button
                  aria-pressed={seasons.includes(option)}
                  className={seasons.includes(option) ? 'is-selected' : ''}
                  key={option}
                  type="button"
                  onClick={() => toggleDetailValue(option, seasons, setSeasons)}
                >
                  {option}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="choice-fieldset">
            <legend>Occasion</legend>
            <div className="choice-chips">
              {OCCASIONS.map((option) => (
                <button
                  aria-pressed={occasions.includes(option)}
                  className={occasions.includes(option) ? 'is-selected' : ''}
                  key={option}
                  type="button"
                  onClick={() => toggleDetailValue(option, occasions, setOccasions)}
                >
                  {option}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="favorite-check">
            <input
              checked={favorite}
              type="checkbox"
              onChange={(event) => setFavorite(event.target.checked)}
            />
            <span className="card-icon card-icon--rose">
              <Icon name="heart" size={20} />
            </span>
            <span>
              <strong>Favourite piece</strong>
              <small>Keep it close when building outfits.</small>
            </span>
          </label>

          {errorMessage ? (
            <p className="form-error" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <div className="form-actions form-actions--split">
            <div>
              <button
                className="text-button"
                disabled={saving}
                type="button"
                onClick={() => void toggleArchive()}
              >
                {item.archived ? 'Restore item' : 'Archive item'}
              </button>
              <button
                className="text-button text-button--danger"
                disabled={saving}
                type="button"
                onClick={() => void removeItem()}
              >
                {confirmingDelete ? 'Tap again to confirm' : 'Delete permanently'}
              </button>
            </div>
            <button className="button button--primary" disabled={saving} type="submit">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SettingsPage({
  user,
  loggingOut,
  onLogout,
}: {
  user: SessionUser;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
}) {
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [privacyMessage, setPrivacyMessage] = useState('');

  async function exportData() {
    setExporting(true);
    setPrivacyMessage('');
    try {
      await downloadPrivateExport();
      setPrivacyMessage('Your private JSON export has downloaded.');
    } catch (caught) {
      setPrivacyMessage(
        caught instanceof Error ? caught.message : 'The export could not download.',
      );
    } finally {
      setExporting(false);
    }
  }

  async function deleteData() {
    if (confirmation !== 'DELETE') return;
    setDeleting(true);
    setPrivacyMessage('');
    try {
      await deletePrivateClosetData();
      setConfirmation('');
      setPrivacyMessage(
        'Wardrobe data and private photos were deleted. Your login remains active.',
      );
    } catch (caught) {
      setPrivacyMessage(
        caught instanceof Error ? caught.message : 'The data could not be deleted.',
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page page--narrow">
      <PageHeader
        eyebrow="Private account"
        title="Settings"
        description="Your wardrobe, privacy, and preferences."
      />
      <section className="settings-card">
        <div className="settings-profile">
          <Avatar name={user.displayName} />
          <div>
            <h2>{user.displayName}</h2>
            <p>@{user.username}</p>
          </div>
        </div>
        <div className="settings-row">
          <span>
            <strong>Private session</strong>
            <small>This device stays signed in while actively used.</small>
          </span>
          <span className="status-pill">Protected</span>
        </div>
        <button
          className="button button--secondary button--wide"
          disabled={loggingOut}
          type="button"
          onClick={() => void onLogout()}
        >
          {loggingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </section>
      <section className="settings-card privacy-card">
        <div>
          <p className="eyebrow">Your data</p>
          <h2>Export or clear your closet</h2>
          <p>
            Export downloads wardrobe metadata, outfits, wear history, plans, and trips. Photos stay
            private and are not placed into a public archive.
          </p>
        </div>
        <button
          className="button button--secondary button--wide"
          disabled={exporting || deleting}
          type="button"
          onClick={() => void exportData()}
        >
          {exporting ? 'Preparing export…' : 'Download my data'}
        </button>
        <div className="danger-zone">
          <div>
            <strong>Delete closet data</strong>
            <p>
              Permanently removes garments, private photos, outfits, wear history, plans, trips, and
              AI analysis history. It does not delete the private login.
            </p>
          </div>
          <label className="field">
            <span>Type DELETE to confirm</span>
            <input
              autoComplete="off"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          <button
            className="button button--danger button--wide"
            disabled={confirmation !== 'DELETE' || deleting || exporting}
            type="button"
            onClick={() => void deleteData()}
          >
            {deleting ? 'Deleting private data…' : 'Delete all closet data'}
          </button>
        </div>
        {privacyMessage ? (
          <p aria-live="polite" className="privacy-message">
            {privacyMessage}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
  action,
  to,
}: {
  icon: IconName;
  title: string;
  description: string;
  action?: string;
  to?: string;
}) {
  return (
    <section className="empty-state">
      <span className="empty-state__icon">
        <Icon name={icon} size={34} />
      </span>
      <p className="eyebrow">A fresh beginning</p>
      <h2>{title}</h2>
      <p>{description}</p>
      {action && to ? (
        <NavLink className="button button--primary" to={to}>
          {action}
          <Icon name="arrow" size={18} />
        </NavLink>
      ) : null}
    </section>
  );
}
